/**
 * Normalização determinística do ditado (spec 009 — assistente de voz).
 *
 * REGRA QUE MANDA NESTE ARQUIVO: a IA transcreve, o código interpreta.
 *
 * "+1,00 −1,00 a 180" e "+1,00 −1,00 a 18" diferem por uma sílaba mal ouvida
 * e por 90° de eixo. Por isso nenhum valor de grau, eixo, adição ou
 * lateralidade vale direto do que o LLM devolveu: tudo passa por aqui, é
 * convertido, validado contra faixa e passo, e **o que não passa vira lacuna**
 * — nunca um valor "arrumado" pelo modelo.
 *
 * O texto original (`heard`) nunca é descartado: é o que o médico lê na fila
 * pra decidir se corrige ou regrava, e é o que torna o erro auditável depois.
 *
 * Este módulo é PURO de propósito: sem Firestore, sem React, sem I/O. O
 * casamento com o banco curado acontece fora (`enrichMedsWithBank` e amigos,
 * em services/firebase/medications.ts) e entra aqui já resolvido como lista de
 * candidatos — assim a regra "match único ou lacuna" fica testável sem rede.
 */

import type { AIExam, AILensRx, AIMedication } from '@/types/ai';
import { mergeObservacoes } from '@/utils/lensObservations';
import type { SurgeryProcedureItem } from '@/utils/surgeryRequest';
import { loadTriggers, phrasesMatch, triggerKey, type TriggerMap } from '@/utils/phraseTriggers';

// ─── Contrato de campo ───

export type VoiceIssue =
  /** Não foi dito (ou a IA não devolveu nada). */
  | 'missing'
  /** Veio da IA mas não se sustenta na transcrição (ancoragem). */
  | 'not_heard'
  /** Número fora da faixa clínica ou fora do passo de 0,25. */
  | 'out_of_range'
  /** Não dá pra saber o que foi dito (sinal ausente, dois números no campo). */
  | 'ambiguous'
  /** Nada no catálogo casou com o que foi ouvido. */
  | 'no_match'
  /** Dois ou mais itens do catálogo casaram igualmente bem. */
  | 'multi_match'
  /** Foi dito, mas não entrou no documento (dias num atestado sem campo de dias). */
  | 'not_applied';

export interface VoiceField<T> {
  /** `null` = lacuna: o campo NÃO é preenchido e o documento não imprime. */
  value: T | null;
  /** O que a IA transcreveu, sempre preservado. */
  heard: string;
  issue?: VoiceIssue;
}

function ok<T>(value: T, heard: string): VoiceField<T> {
  return { value, heard };
}

function gap<T>(issue: VoiceIssue, heard: string): VoiceField<T> {
  return { value: null, heard, issue };
}

// ─── Refração ───

export type RefractionKind = 'esferica' | 'cilindrica' | 'eixo' | 'adicao';

/** Faixa clínica por campo. Fora disso é erro de transcrição, não grau raro. */
const RANGE: Record<RefractionKind, { min: number; max: number }> = {
  esferica: { min: -30, max: 30 },
  cilindrica: { min: -12, max: 0 },
  eixo: { min: 0, max: 180 },
  adicao: { min: 0, max: 4 },
};

/** Palavras que o médico fala em volta do número e não fazem parte dele. */
const NOISE = /\b(graus?|dioptrias?|eixo|de|a|em|x|esf(erico)?|cil(indrico)?|adicao)\b/gi;

/** "plano"/"neutro" = zero dióptrico. */
const PLANO = /^(plano|plana|neutro|zero|nulo)$/i;

function stripAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Converte um campo cru de refração em número validado.
 *
 * Decisões deliberadas, todas do lado de exigir um clique em vez de adivinhar:
 *
 * - **Sinal ausente em esférico/cilíndrico não-zero ⇒ `ambiguous`.** Se a
 *   transcrição perdeu o "+"/"−", não dá pra recuperar; chutar troca hipermetropia
 *   por miopia. (Adição é sempre positiva por convenção, então passa sem sinal.)
 * - **Cilíndrico positivo ⇒ `out_of_range`.** Nada de transpor sozinho: inverter
 *   eixo automaticamente é pior que pedir confirmação.
 * - **Dois números no mesmo campo ⇒ `ambiguous`.** "1,00 180" no esférico é
 *   sintoma de que a IA colou dois campos.
 * - **Fora do passo de 0,25 ⇒ `out_of_range`.** Lente não existe em 1,30.
 */
export function parseRefractionToken(raw: string, kind: RefractionKind): VoiceField<number> {
  const heard = (raw ?? '').trim();
  if (!heard) return gap('missing', heard);

  const cleaned = stripAccents(heard)
    .replace(/[°º]/g, ' ')
    // Sinal ditado por extenso ("menos um e vinte e cinco" chega às vezes
    // como "menos 1,25"). Vira símbolo antes da limpeza pra não virar lacuna.
    .replace(/\bmenos\b/gi, '-')
    .replace(/\bmais\b/gi, '+')
    .replace(NOISE, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (PLANO.test(cleaned)) {
    return kind === 'eixo' ? ok(0, heard) : ok(0, heard);
  }

  // Captura número com sinal opcional; vírgula e ponto valem como decimal.
  const matches = cleaned.match(/[+\-−–]?\s*\d+(?:[.,]\d+)?/g);
  if (!matches || matches.length === 0) return gap('ambiguous', heard);
  if (matches.length > 1) return gap('ambiguous', heard);

  const token = matches[0].replace(/\s+/g, '');
  const signed = /^[+\-−–]/.test(token);
  const negative = /^[\-−–]/.test(token);
  const num = Number(token.replace(/^[+\-−–]/, '').replace(',', '.'));
  if (!Number.isFinite(num)) return gap('ambiguous', heard);

  const value = negative ? -num : num;
  const { min, max } = RANGE[kind];

  if (kind === 'eixo') {
    if (!Number.isInteger(value)) return gap('out_of_range', heard);
    if (value < min || value > max) return gap('out_of_range', heard);
    return ok(value, heard);
  }

  // Sinal obrigatório quando o valor não é zero (exceto adição, sempre +).
  if (kind !== 'adicao' && value !== 0 && !signed) return gap('ambiguous', heard);

  if (value < min || value > max) return gap('out_of_range', heard);
  // Passo de 0,25 — comparação em centésimos pra fugir de float.
  if (Math.round(value * 100) % 25 !== 0) return gap('out_of_range', heard);

  return ok(value, heard);
}

export interface VoiceEyeRx {
  esferica: VoiceField<number>;
  cilindrica: VoiceField<number>;
  eixo: VoiceField<number>;
  adicao: VoiceField<number>;
  /** Não são campos de risco: passam como texto, sem validação. */
  dp: string;
  av: string;
}

export interface VoiceLensRx {
  od: VoiceEyeRx;
  oe: VoiceEyeRx;
  observacoes: string;
}

const EMPTY_EYE = { esferica: '', cilindrica: '', eixo: '', adicao: '', dp: '', av: '' };

function normalizeEye(raw: AILensRx['od'] | undefined): VoiceEyeRx {
  const src = raw ?? EMPTY_EYE;
  const esferica = parseRefractionToken(src.esferica, 'esferica');
  const cilindrica = parseRefractionToken(src.cilindrica, 'cilindrica');
  let eixo = parseRefractionToken(src.eixo, 'eixo');

  // Cilindro sem eixo é receita inválida: o eixo vira lacuna obrigatória.
  // (Cilindro ausente ou zero ⇒ eixo é irrelevante e não vira pendência.)
  const hasCyl = cilindrica.value !== null && cilindrica.value !== 0;
  if (!hasCyl && eixo.issue === 'missing') {
    eixo = { value: null, heard: eixo.heard };
  }

  return {
    esferica,
    cilindrica,
    eixo,
    adicao: parseRefractionToken(src.adicao, 'adicao'),
    dp: (src.dp ?? '').trim(),
    av: (src.av ?? '').trim(),
  };
}

/**
 * Contexto do DITADO que gerou a receita. Só existe quando a receita veio
 * da voz (ditado ou ao vivo); a edição manual do card não passa por aqui —
 * o que o médico digita num olho só fica naquele olho.
 */
export interface LensDictation {
  /** Transcrição literal da fala, pra saber se a adição foi dita COM olho. */
  transcricao?: string;
}

/** Palavras de olho que, coladas à "adição", fazem dela um pedido de UM olho. */
const OLHO = '(?:od|oe|olho direito|olho esquerdo|direito|esquerdo)';
// "adição só no OD", "adição OE de 2,00", "adição de dois no olho direito".
const RE_ADICAO_DEPOIS_OLHO = new RegExp(`\\badicao\\b[^.;\\n]{0,30}?\\b${OLHO}\\b`, 'i');
// "OD adição 2" / "OE, adição 2,50" — olho IMEDIATAMENTE antes. "OD +1,00
// -1,00 a 180 adição 2" NÃO conta: o olho ali abre a refração, não a adição.
const RE_OLHO_ANTES_ADICAO = new RegExp(`\\b${OLHO}\\b[\\s,:]{0,3}\\badicao\\b`, 'i');

/**
 * O médico nomeou um olho junto da adição? ("adição só no OD", "adição OE de
 * 2,00"). Sem transcrição, não há como afirmar — devolve `false`, e a adição
 * vale pros dois olhos, que é o caso comum.
 */
export function adicaoComOlhoExplicito(transcricao: string | null | undefined): boolean {
  const fala = stripAccents(transcricao ?? '').toLowerCase();
  if (!fala.includes('adicao')) return false;
  return RE_ADICAO_DEPOIS_OLHO.test(fala) || RE_OLHO_ANTES_ADICAO.test(fala);
}

/** Campo que não foi dito: lacuna 'missing' ou vazio sem pendência. */
function naoDito(f: VoiceField<number>): boolean {
  return f.value === null && (!f.issue || f.issue === 'missing');
}

/**
 * Adição ditada sem olho ("adição de +2,00", "adição dois") vale pros DOIS
 * olhos — presbiopia é bilateral e é assim que o médico dita. O modelo às
 * vezes põe o valor num olho só; aqui a cópia é determinística: se só um olho
 * tem adição válida e o outro não recebeu nada (com ou sem refração), copia,
 * herdando o `heard` do olho ditado — a cópia não é lacuna nem invenção.
 *
 * Respeita o olho quando foi nomeado junto da adição ("adição só no OD").
 * Adição ouvida e não entendida no outro olho (fora da faixa, ambígua) NÃO é
 * sobrescrita: a pendência é a informação que o médico precisa ver.
 */
export function propagateAdicao(voice: VoiceLensRx, transcricao?: string | null): VoiceLensRx {
  if (adicaoComOlhoExplicito(transcricao)) return voice;
  const od = voice.od.adicao;
  const oe = voice.oe.adicao;
  if (od.value !== null && naoDito(oe)) {
    return { ...voice, oe: { ...voice.oe, adicao: { value: od.value, heard: od.heard } } };
  }
  if (oe.value !== null && naoDito(od)) {
    return { ...voice, od: { ...voice.od, adicao: { value: oe.value, heard: oe.heard } } };
  }
  return voice;
}

/**
 * JSON da IA → receita validada. Com `ditado`, a receita veio da voz e a
 * adição sem olho é propagada pros dois olhos (`propagateAdicao`). Sem ele
 * (edição manual do card), cada olho fica exatamente como veio.
 */
export function normalizeLensRx(rx: AILensRx | null | undefined, ditado?: LensDictation): VoiceLensRx | null {
  if (!rx) return null;
  const voice: VoiceLensRx = {
    od: normalizeEye(rx.od),
    oe: normalizeEye(rx.oe),
    observacoes: (rx.observacoes ?? '').trim(),
  };
  return ditado ? propagateAdicao(voice, ditado.transcricao) : voice;
}

// ─── Formatação de volta pro formato dos modais ───

/** "+1,00" / "-1,25" / "0,00" — o formato que os modais e o PDF já usam. */
export function formatDiopter(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(value).toFixed(2).replace('.', ',')}`;
}

function eyeToStrings(eye: VoiceEyeRx): AILensRx['od'] {
  return {
    esferica: eye.esferica.value === null ? '' : formatDiopter(eye.esferica.value),
    cilindrica: eye.cilindrica.value === null ? '' : formatDiopter(eye.cilindrica.value),
    eixo: eye.eixo.value === null ? '' : String(eye.eixo.value),
    adicao: eye.adicao.value === null ? '' : formatDiopter(eye.adicao.value),
    dp: eye.dp,
    av: eye.av,
  };
}

/**
 * Converte de volta pro `AILensRx` que os modais e o `buildLensBodyHtml` já
 * consomem. **Lacuna sai como string vazia** — o campo fica em branco pro
 * médico preencher, nunca com um valor inventado.
 */
export function toLensRx(voice: VoiceLensRx): AILensRx {
  return {
    od: eyeToStrings(voice.od),
    oe: eyeToStrings(voice.oe),
    observacoes: voice.observacoes,
  };
}

// ─── Catálogo: match único ou lacuna ───

export interface CatalogCandidate<T> {
  /** Rótulo exibido na fila quando o médico precisa escolher. */
  label: string;
  /** Score do rerank já existente (`scoreHit` em services/firebase/medications). */
  score: number;
  item: T;
}

/**
 * Aplica a regra "match único ou lacuna": nunca escolhemos o "mais provável".
 *
 * - nenhum candidato ⇒ `no_match`;
 * - empate no topo ⇒ `multi_match` (o médico escolhe na fila);
 * - um vencedor claro ⇒ preenche.
 */
export function resolveCatalogMatch<T>(
  heard: string,
  candidates: CatalogCandidate<T>[],
): VoiceField<T> {
  const name = (heard ?? '').trim();
  if (!name) return gap('missing', name);
  if (!candidates || candidates.length === 0) return gap('no_match', name);

  const ranked = candidates.slice().sort((a, b) => b.score - a.score);
  if (ranked.length > 1 && ranked[1].score === ranked[0].score) {
    return gap('multi_match', name);
  }
  return ok(ranked[0].item, name);
}

// ─── Lateralidade ───

export type Laterality = 'OD' | 'OE' | 'AO';

const RE_AO = /\b(ao|ambos os olhos|ambos|bilateral|bilaterais|olhos)\b/i;
const RE_OD = /\b(od|olho direito|direito)\b/i;
// Atenção: "OS" (oculus sinister) NÃO entra aqui — casaria com o artigo "os"
// de qualquer frase ("remover os tecidos") e marcaria olho esquerdo sozinho.
// Lateralidade errada é o pior defeito possível deste módulo.
const RE_OE = /\b(oe|olho esquerdo|esquerdo)\b/i;

/**
 * Extrai a lateralidade de um texto ditado.
 *
 * Lateralidade é onde o erro acontece (a lição do `batchDocuments.ts`), então
 * aqui não existe default: não dito ⇒ `missing`; OD e OE citados no mesmo
 * texto ⇒ `ambiguous` (pode ser "AO", pode ser a IA misturando dois pedidos).
 */
export function parseLaterality(raw: string): VoiceField<Laterality> {
  const heard = (raw ?? '').trim();
  if (!heard) return gap('missing', heard);

  const text = stripAccents(heard);
  if (RE_AO.test(text)) return ok('AO', heard);

  const od = RE_OD.test(text);
  const oe = RE_OE.test(text);
  if (od && oe) return gap('ambiguous', heard);
  if (od) return ok('OD', heard);
  if (oe) return ok('OE', heard);
  return gap('missing', heard);
}

// ─── Rascunhos da fila ───

export type VoiceDraftKind =
  | 'receitaOculos'
  | 'receitaMedicamentos'
  | 'solicitacaoExames'
  | 'solicitacaoCirurgia'
  | 'atestadoLaudo';

export interface VoiceGap {
  /** Caminho do campo, pra fila destacar (ex.: "od.eixo"). */
  field: string;
  label: string;
  heard: string;
  issue: VoiceIssue;
}

const ISSUE_LABEL: Record<VoiceIssue, string> = {
  missing: 'não foi dito',
  not_heard: 'confira: pode não ter sido dito',
  out_of_range: 'fora da faixa esperada',
  ambiguous: 'não dá pra saber o que foi dito',
  no_match: 'não achei no seu catálogo',
  multi_match: 'mais de uma opção casou',
  not_applied: 'não entrou no documento — confira',
};

export function describeIssue(issue: VoiceIssue): string {
  return ISSUE_LABEL[issue];
}

function collectEyeGaps(prefix: string, eye: VoiceEyeRx): VoiceGap[] {
  const out: VoiceGap[] = [];
  const push = (field: string, label: string, f: VoiceField<number>) => {
    if (f.issue) out.push({ field: `${prefix}.${field}`, label, heard: f.heard, issue: f.issue });
  };
  push('esferica', `${prefix.toUpperCase()} esférico`, eye.esferica);
  // Cilindro AUSENTE não é pendência: receita só esférica é rotina, e o
  // médico costuma dizer "sem grau cilíndrico" — acusar "não foi dito" nesse
  // caso é o sistema chamando de lacuna uma informação que ele recebeu.
  // Cilindro DITO e não entendido (fora de faixa, sem sinal) continua sendo.
  if (eye.cilindrica.issue && eye.cilindrica.issue !== 'missing') {
    push('cilindrica', `${prefix.toUpperCase()} cilíndrico`, eye.cilindrica);
  }
  push('eixo', `${prefix.toUpperCase()} eixo`, eye.eixo);
  if (eye.adicao.issue && eye.adicao.issue !== 'missing') {
    push('adicao', `${prefix.toUpperCase()} adição`, eye.adicao);
  }
  return out;
}

/** Lacunas de uma receita de óculos. Adição ausente não é pendência. */
export function lensRxGaps(voice: VoiceLensRx): VoiceGap[] {
  return [...collectEyeGaps('od', voice.od), ...collectEyeGaps('oe', voice.oe)];
}

/**
 * Escolha que o sistema fez SOZINHO entre opções do cadastro — o Jev decide
 * (spec 012, pedido do Leopoldo de 25/10/2026: "não precisar clicar nestas
 * decisões … caso haja necessidade de mudança, eu faço manualmente depois").
 * O card mostra a marca e as outras opções pra trocar com um clique.
 *
 * Sem `por`: dúvida ainda não decidida (o passo do Jev pega antes de o card
 * entrar na fila; com o Jev desligado fica assim, calada).
 */
export interface VoiceEscolha {
  /** Quem decidiu: o Jev, ou a regra (número dito, ou Jev sem resposta). */
  por?: 'jev' | 'regra';
  /** O que foi ouvido/escrito pro item. */
  ouvido: string;
  /** As opções que estavam em jogo (nomes, na ordem). */
  opcoes: string[];
  /** Valia "nenhuma destas" (o item ficava como foi dito). */
  nenhuma?: boolean;
  /**
   * As opções eram a lista inteira da clínica (nada em comum com o dito):
   * o card não oferece todas pra trocar, só a volta pro que foi dito.
   */
  listaInteira?: boolean;
}

export interface VoiceMedicationDraft {
  /** Medicamento resolvido no catálogo (ou `null`, com a lacuna ao lado). */
  match: VoiceField<AIMedication>;
  /**
   * Empate no topo (`multi_match`): as opções empatadas, pra o médico
   * escolher com um clique no card em vez de abrir o editor. Continuam
   * aqui depois que o Jev escolhe (`escolha`), pra trocar com um clique.
   */
  options?: Array<{ label: string; item: AIMedication }>;
  escolha?: VoiceEscolha;
}

/** Lacunas de uma receita de medicamentos. */
export function medicationGaps(items: VoiceMedicationDraft[]): VoiceGap[] {
  return items.flatMap((item, i) =>
    item.match.issue
      ? [{ field: `medicamentos[${i}]`, label: `Medicamento "${item.match.heard}"`, heard: item.match.heard, issue: item.match.issue }]
      : [],
  );
}

/** Exame sem nome não vira linha de pedido. */
export function examGaps(exams: AIExam[]): VoiceGap[] {
  return exams.flatMap((e, i) =>
    e.nome.trim()
      ? []
      : [{ field: `exames[${i}]`, label: `Exame ${i + 1}`, heard: '', issue: 'missing' as VoiceIssue }],
  );
}

export interface VoiceSurgeryDraft {
  texto: string;
  lateralidade: VoiceField<Laterality>;
  /** O texto virou um procedimento cadastrado escolhido pelo Jev (`ouvido` = como veio). */
  escolha?: VoiceEscolha;
  /**
   * Procedimento ESTRUTURADO (olho, prazo, motivo, CID) — nasce quando o
   * médico edita o card no mesmo editor do modal manual de Solicitações.
   * Com ele, o registro no prontuário sai reabrível em campos, igual ao
   * pedido feito à mão. Ditado puro não tem (só texto + lateralidade).
   */
  proc?: SurgeryProcedureItem;
}

/**
 * Solicitação de cirurgia: o texto do procedimento vem da IA, mas a
 * lateralidade é obrigatória — sem ela o card não imprime.
 */

/**
 * Separa um texto de cirurgia em procedimentos independentes.
 *
 * "Pterígio em olho direito e catarata em olho esquerdo" são DOIS pedidos:
 * cirurgias diferentes, olhos diferentes, autorizações diferentes. Num card
 * só, a lateralidade fica ambígua (OD e OE na mesma frase) e o documento
 * trava — além de sair uma solicitação única para dois procedimentos.
 *
 * A divisão é conservadora, e essa é a regra que importa: quebrar por " e "
 * SÓ acontece quando cada metade resultante fica com exatamente UMA
 * lateralidade. "Exérese de pterígio e transplante de córnea em OD" não é
 * dividido, porque a divisão não melhoraria nada e inventaria um pedido.
 */
export function splitSurgeries(texto: string | null | undefined): string[] {
  const bruto = (texto ?? '').trim();
  if (!bruto) return [];

  // Separadores explícitos: o prompt pede uma cirurgia por linha.
  const partes = bruto
    .split(/[\n;]+|\s+\/\s+/)
    .map((t) => t.replace(/^[-•*\s]+/, '').trim())
    .filter(Boolean);

  return partes.flatMap((parte) => {
    if (parseLaterality(parte).issue !== 'ambiguous') return [parte];
    // Dois olhos na mesma frase: tenta " e ", aceitando só se resolver.
    const metades = parte.split(/\s+e\s+/i).map((t) => t.trim()).filter(Boolean);
    if (metades.length < 2) return [parte];
    const cadaUmaResolvida = metades.every((m) => {
      const lat = parseLaterality(m);
      return lat.value !== null;
    });
    return cadaUmaResolvida ? metades : [parte];
  });
}

export function normalizeSurgery(texto: string | null | undefined): VoiceSurgeryDraft | null {
  const t = (texto ?? '').trim();
  if (!t) return null;
  return { texto: t, lateralidade: parseLaterality(t) };
}

export function surgeryGaps(draft: VoiceSurgeryDraft, index = 0): VoiceGap[] {
  const f = draft.lateralidade;
  if (!f.issue) return [];
  // A lateralidade é lida do texto inteiro do pedido. Repetir esse texto como
  // "ouvi …" na pendência só polui — o médico já está lendo o pedido acima.
  const heard = f.issue === 'missing' ? '' : f.heard;
  return [{
    field: `cirurgias[${index}].lateralidade`,
    label: `Lateralidade (OD/OE/AO) — ${draft.texto}`,
    heard,
    issue: f.issue,
  }];
}

/** Texto do pedido com a lateralidade explícita (não depende de quem lê deduzir o olho). */
export function surgeryLine(s: VoiceSurgeryDraft): string {
  const lado = s.lateralidade.value;
  return lado && !new RegExp(`\\b${lado}\\b`, 'i').test(s.texto) ? `${s.texto} — ${lado}` : s.texto;
}

/**
 * Refração ditada em frases separadas ("OD +1,00 -1,00 a 180." pausa
 * "OE +1,25 -1,25 a 10." pausa "adição +2,00") é UMA receita. Campo que o
 * ditado novo NÃO mencionou (`issue: 'missing'`) fica como estava; campo
 * mencionado de novo substitui — ditar de novo é corrigir. Decisão do
 * Leopoldo, 16/09/2026.
 */
export function mergeLensRx(existing: VoiceLensRx, incoming: VoiceLensRx): VoiceLensRx {
  // "Não dito" = lacuna 'missing' OU vazio sem pendência (eixo sem cilindro
  // sai assim do normalizeEye). Valor ouvido e não entendido (ambiguous,
  // out_of_range) É um ditado novo e substitui — a pendência aparece.
  // A adição chega aqui já propagada pelos dois olhos quando foi dita sem
  // olho (`propagateAdicao`, no normalizeLensRx da frase nova); a fusão só
  // sobrepõe o que a frase nova trouxe — "adição só no OD" continua só no OD.
  const pick = (a: VoiceField<number>, b: VoiceField<number>) => (naoDito(b) ? a : b);
  const eye = (a: VoiceEyeRx, b: VoiceEyeRx): VoiceEyeRx => {
    const cilindrica = pick(a.cilindrica, b.cilindrica);
    let eixo = pick(a.eixo, b.eixo);
    // Mesma regra do normalizeEye: sem cilindro, eixo ausente não é lacuna.
    const hasCyl = cilindrica.value !== null && cilindrica.value !== 0;
    if (!hasCyl && eixo.issue === 'missing') eixo = { value: null, heard: eixo.heard };
    if (hasCyl && eixo.value === null && !eixo.issue) eixo = { value: null, heard: eixo.heard, issue: 'missing' };
    return {
      esferica: pick(a.esferica, b.esferica),
      cilindrica,
      eixo,
      adicao: pick(a.adicao, b.adicao),
      dp: b.dp || a.dp,
      av: b.av || a.av,
    };
  };
  return {
    od: eye(existing.od, incoming.od),
    oe: eye(existing.oe, incoming.oe),
    observacoes: mergeObservacoes(existing.observacoes, incoming.observacoes),
  };
}


// ─── Retorno / próxima avaliação ────────────────────────────────────────────

/**
 * "Retorno para mostrar exames até dezembro de 2026" (spec 009).
 *
 * É a mesma linha que o card "Próxima Avaliação" do pedido de exames gera à
 * mão: motivo + mês, impressa abaixo da tabela de exames e, opcionalmente,
 * gravada em `patient.recommendedReturnAt` pro alerta do portal. O mês é
 * parseado aqui, determinístico — o LLM sugere, a faixa decide.
 */
export interface VoiceReturnDraft {
  /** Motivo CANÔNICO da clínica ("Retorno para mostrar exames", "Nova consulta"). */
  motivo: string;
  /** 'YYYY-MM'. Lacuna se não deu pra entender ou se já passou. */
  mes: VoiceField<string>;
  /**
   * O motivo não veio da fala: é o padrão (nada casou com a lista, ou nada
   * foi dito). Um pacote de solicitação pode trocá-lo pelo dele.
   */
  motivoPadrao?: boolean;
  /** O que foi dito e NÃO casou com a lista — vira pendência pra conferir. */
  motivoOuvido?: string;
  /**
   * Os dois padrões DA LISTA (com e sem exame no pedido), guardados junto:
   * no Ao vivo a fala do retorno e a dos exames chegam em chamadas
   * separadas (ou os exames vêm de um pacote), então quem decide o padrão é
   * o card FINAL — `motivoPeloCard`. Caso de campo (08/10/2026):
   * "reavaliação para possibilidade de cirurgia" ao lado de 4 exames saiu
   * "Retorno (acompanhamento)" em vez de "Retorno para mostrar exames".
   */
  padroes?: { comExames?: string; semExames?: string };
  /** O motivo dito fora da lista foi decidido sozinho (o Jev). */
  escolha?: VoiceEscolha;
}

const MESES_PT: Record<string, number> = {
  janeiro: 1, fevereiro: 2, marco: 3, abril: 4, maio: 5, junho: 6,
  julho: 7, agosto: 8, setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
  jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6, jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12,
};

function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** 'YYYY-MM' do mês de `d` no fuso de Brasília. */
export function isoMonthOf(d: Date): string {
  return d.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
}

/**
 * Aceita 'YYYY-MM', 'MM/YYYY', 'dezembro de 2026', 'dez/2026'. Devolve
 * lacuna (`value: null`) quando não entende ou quando o mês já passou —
 * retorno no passado é sempre erro de transcrição, nunca intenção.
 */
export function parseReturnMonth(heard: string, hoje: Date = new Date()): VoiceField<string> {
  const raw = (heard ?? '').trim();
  if (!raw) return { value: null, heard: raw, issue: 'missing' };
  let y = 0;
  let m = 0;
  let hit = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (hit) { y = +hit[1]; m = +hit[2]; }
  if (!y) {
    hit = raw.match(/^(\d{1,2})\/(\d{4})$/);
    if (hit) { y = +hit[2]; m = +hit[1]; }
  }
  if (!y) {
    hit = semAcento(raw).match(/([a-z]{3,9})\.?\s*(?:de|\/|-)?\s*(\d{4})/);
    if (hit && MESES_PT[hit[1]]) { y = +hit[2]; m = MESES_PT[hit[1]]; }
  }
  if (!y || m < 1 || m > 12) return { value: null, heard: raw, issue: 'ambiguous' };
  const iso = `${y}-${String(m).padStart(2, '0')}`;
  if (iso < isoMonthOf(hoje)) return { value: null, heard: raw, issue: 'out_of_range' };
  return ok(iso, raw);
}

export interface ReturnReasonOptions {
  /** Motivos canônicos da clínica (`examChecklist.evaluationReasons`). */
  canonicalReasons?: string[];
  /** Gatilhos de frase por motivo ("trazer" → "Retorno para mostrar exames"). */
  reasonTriggers?: TriggerMap;
  /** Motivo quando nada casou. Default: `defaultReturnReason(lista, false)`. */
  fallbackReason?: string;
}

/** "Retorno" sozinho não diz QUAL retorno: decide o contexto, não a palavra. */
const MOTIVO_GENERICO = new Set(['retorno', 'retornar', 'volta', 'voltar', 'reconsulta']);

/**
 * Motivo padrão da lista quando o ditado não disse qual: com exame no
 * pedido é "Retorno para mostrar exames"; sem, acompanhamento. Sempre um
 * item da lista (pedido do Leopoldo, 27/09/2026: o motivo "deve ser de
 * acordo com as opções do seletor").
 */
export function defaultReturnReason(reasons: string[], temExames: boolean): string | undefined {
  const acha = (re: RegExp) => reasons.find((r) => re.test(semAcento(r)));
  return (temExames ? acha(/mostrar exames|trazer exames|com exames/) : undefined)
    ?? acha(/acompanhamento/)
    ?? acha(/^retorno/)
    ?? reasons[0];
}

/**
 * Motivo ouvido → motivo CANÔNICO da clínica.
 *
 * "trazer até março de 2027" tem que virar "Retorno para mostrar exames",
 * porque é assim que está escrito na lista de motivos da Próxima Avaliação e
 * é assim que o pedido feito à mão imprime. Ordem: gatilho de frase primeiro
 * (é como ele fala), depois casamento por palavras com o motivo canônico.
 * Nada casou ⇒ fica o que foi dito. Pedido do Leopoldo, 19/09/2026.
 */
export function canonicalReturnReason(
  heard: string,
  opts: ReturnReasonOptions = {},
): string | null {
  const reasons = opts.canonicalReasons ?? [];
  if (reasons.length === 0 || !heard.trim()) return null;
  // Sem gatilhos explícitos valem os do navegador (com as sementes): é o que
  // faz "trazer" funcionar no primeiro ditado, sem configurar nada.
  const triggers = opts.reasonTriggers ?? loadTriggers('returnReason');
  const porGatilho = reasons.find((r) => phrasesMatch(heard, triggers[triggerKey(r)] ?? []));
  if (porGatilho) return porGatilho;
  const falado = semAcento(heard).replace(/[^a-z0-9]+/g, ' ').trim();
  if (!falado || MOTIVO_GENERICO.has(falado)) return null;
  const porPalavra = reasons.find((r) => {
    const alvo = semAcento(r).replace(/[^a-z0-9]+/g, ' ').trim();
    return alvo === falado || falado.includes(alvo) || alvo.includes(falado);
  });
  return porPalavra ?? null;
}

export function normalizeReturn(
  r: { motivo?: string; mes?: string } | null | undefined,
  hoje: Date = new Date(),
  opts: ReturnReasonOptions = {},
): VoiceReturnDraft | null {
  if (!r || typeof r !== 'object') return null;
  // O modelo às vezes manda a frase inteira como motivo ("trazer até
  // fevereiro de 2027"); a linha impressa já acrescenta "até <mês>", então o
  // "até …" final sai daqui — senão imprimia "até fevereiro até fevereiro".
  let motivo = (r.motivo ?? '').trim()
    .replace(/^(até|ate)\s+.*$/i, '')
    .replace(/\s+(até|ate|em|no mês de)\s+\S.*$/i, '')
    .trim();
  motivo = motivo.replace(/[.\s]+$/, '');
  if (motivo) motivo = motivo.charAt(0).toUpperCase() + motivo.slice(1);
  const mes = (r.mes ?? '').trim();
  if (!motivo && !mes) return null;
  // A forma canônica da clínica manda sobre o verbo solto que foi ouvido.
  const canonico = canonicalReturnReason((r.motivo ?? '').trim() || motivo, opts);
  const parsed = parseReturnMonth(mes, hoje);
  if (canonico) return { motivo: canonico, mes: parsed };
  const reasons = opts.canonicalReasons ?? [];
  // Sem lista (nada configurado): fica o que foi dito, como sempre foi.
  if (reasons.length === 0) return { motivo: motivo || 'Retorno', mes: parsed };
  // Com lista, o papel só leva motivo DA LISTA (27/09/2026). O que foi dito
  // e não casou fica guardado pra virar pendência — "Retorno" sozinho não.
  const ouvido = motivo && !MOTIVO_GENERICO.has(semAcento(motivo).replace(/[^a-z0-9]+/g, ' ').trim())
    ? motivo
    : '';
  return {
    motivo: opts.fallbackReason ?? defaultReturnReason(reasons, false) ?? 'Retorno',
    mes: parsed,
    motivoPadrao: true,
    padroes: { comExames: defaultReturnReason(reasons, true), semExames: defaultReturnReason(reasons, false) },
    ...(ouvido ? { motivoOuvido: ouvido } : {}),
  };
}

/**
 * Motivo PADRÃO decidido pelo card inteiro: com exame no pedido, "mostrar
 * exames"; sem, acompanhamento (ver `padroes`). Motivo que casou com a
 * lista, veio do pacote ou foi escolhido à mão não é padrão — não muda.
 */
export function motivoPeloCard(
  r: VoiceReturnDraft | null | undefined,
  temExames: boolean,
): VoiceReturnDraft | null {
  if (!r) return null;
  if (!r.motivoPadrao || !r.padroes) return r;
  const motivo = (temExames ? r.padroes.comExames : r.padroes.semExames) ?? r.motivo;
  return motivo === r.motivo ? r : { ...r, motivo };
}

export function returnGaps(r: VoiceReturnDraft | null | undefined): VoiceGap[] {
  if (!r) return [];
  const gaps: VoiceGap[] = [];
  if (r.motivoOuvido) {
    gaps.push({ field: 'retorno.motivo', label: `Motivo do retorno (usei "${r.motivo}")`, heard: r.motivoOuvido, issue: 'no_match' });
  }
  if (r.mes.value === null) {
    gaps.push({ field: 'retorno.mes', label: 'Mês do retorno', heard: r.mes.heard, issue: r.mes.issue ?? 'missing' });
  }
  return gaps;
}

/** 'YYYY-MM' → "dezembro de 2026". */
export function isoMonthToBR(iso: string): string {
  const [y, m] = iso.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

/** A linha impressa, igual à do card "Próxima Avaliação". Vazia se houver lacuna. */
export function returnLine(r: VoiceReturnDraft | null | undefined): string {
  if (!r || r.mes.value === null) return '';
  return `${r.motivo} até ${isoMonthToBR(r.mes.value)}.`;
}

/**
 * A orientação ditada é SÓ a repetição do prazo que já virou Próxima
 * avaliação? ("para trazer daqui a 1 mês", com o retorno já marcado.)
 *
 * Visto em campo (20/09/2026): a mesma informação saía duas vezes no papel,
 * uma nas orientações e outra na linha de retorno. Só descarta quando não
 * sobra NENHUMA palavra de conteúdo além de verbo de retorno, tempo e
 * artigo; qualquer instrução de verdade ("enviar pelo WhatsApp") fica.
 */
const VERBOS_RETORNO = new Set([
  'trazer', 'traga', 'retornar', 'retorno', 'voltar', 'volte', 'mostrar',
  'mostre', 'rever', 'revisar', 'revisao', 'reavaliar', 'reavaliacao',
]);
const PALAVRAS_TEMPO = new Set([
  'ate', 'em', 'daqui', 'a', 'de', 'do', 'da', 'no', 'na', 'para', 'pra', 'o', 'os',
  'as', 'um', 'uma', 'mes', 'meses', 'ano', 'anos', 'dia', 'dias', 'semana', 'semanas',
  'proximo', 'proxima', 'que', 'vem', 'exame', 'exames', 'com',
  'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto',
  'setembro', 'outubro', 'novembro', 'dezembro',
]);

export function isOnlyReturnDeadlinePhrase(texto: string): boolean {
  const limpo = semAcento(texto ?? '').replace(/[^a-z0-9]+/g, ' ').trim();
  if (!limpo) return false;
  const tokens = limpo.split(' ').filter(Boolean);
  // Precisa MENCIONAR tempo; senão é orientação comum e não tem por que sumir.
  const temTempo = tokens.some((t) => /^\d+$/.test(t) || PALAVRAS_TEMPO.has(t));
  if (!temTempo) return false;
  const conteudo = tokens.filter(
    (t) => !/^\d+$/.test(t) && !PALAVRAS_TEMPO.has(t) && !VERBOS_RETORNO.has(t),
  );
  return conteudo.length === 0;
}

/**
 * Prazo dito em linguagem de consultório → 'YYYY-MM'.
 *
 * "mês que vem", "próximo mês", "daqui a 3 meses", "em 1 ano", "15 dias",
 * ou um mês explícito ("até março de 2027"). Null quando não dá pra
 * calcular. Existe pra a frase de prazo que o modelo deixou solta nas
 * orientações virar Próxima avaliação sozinha (pedido de 20/09/2026).
 */
export function parseRelativeMonth(texto: string, hoje: Date = new Date()): string | null {
  const t = semAcento(texto ?? '').replace(/[^a-z0-9]+/g, ' ').trim();
  if (!t) return null;
  const explicito = parseReturnMonth(texto, hoje);
  if (explicito.value) return explicito.value;
  const base = new Date(hoje);
  const soma = (n: number, unidade: string): string => {
    const d = new Date(base);
    if (/^dia/.test(unidade)) d.setDate(d.getDate() + n);
    else if (/^semana/.test(unidade)) d.setDate(d.getDate() + 7 * n);
    else if (/^ano/.test(unidade)) d.setMonth(d.getMonth() + 12 * n);
    else d.setMonth(d.getMonth() + n);
    return isoMonthOf(d);
  };
  if (/\b(mes que vem|proximo mes|no mes seguinte)\b/.test(t)) return soma(1, 'mes');
  if (/\b(ano que vem|proximo ano)\b/.test(t)) return soma(12, 'mes');
  const hit = t.match(/\b(\d{1,2})\s*(dias?|semanas?|mes(?:es)?|anos?)\b/);
  if (hit) return soma(Number(hit[1]), hit[2]);
  const porExtenso: Record<string, number> = {
    um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6,
    sete: 7, oito: 8, nove: 9, dez: 10, doze: 12, quinze: 15, vinte: 20, trinta: 30,
  };
  const hit2 = t.match(/\b(um|uma|dois|duas|tres|quatro|cinco|seis|sete|oito|nove|dez|doze|quinze|vinte|trinta)\s+(dias?|semanas?|mes(?:es)?|anos?)\b/);
  if (hit2) return soma(porExtenso[hit2[1]], hit2[2]);
  return null;
}

/** 'YYYY-MM' → epoch ms da meia-noite do dia 1 em Brasília (mesma regra do portal). */
export function isoMonthToFirstDayMs(iso: string): number {
  return new Date(`${iso}-01T00:00:00-03:00`).getTime();
}
